import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { ProgressDots } from '../../components/ProgressDots.js';
import { useOnboardingProfile } from '../../hooks/useOnboarding.js';
import { type OnboardingStep, useOnboardingStore } from '../../stores/onboardingStore.js';

const TOTAL_STEPS = 6;

const PATH_TO_STEP: Record<string, OnboardingStep> = {
  '/onboarding/mode': 'mode',
  '/onboarding/goal': 'goal',
  '/onboarding/body-stats': 'body_stats',
  '/onboarding/activity': 'activity',
  '/onboarding/training': 'training',
  '/onboarding/summary': 'summary',
};

const STEP_TO_PATH: Record<OnboardingStep, string> = {
  mode: '/onboarding/mode',
  goal: '/onboarding/goal',
  body_stats: '/onboarding/body-stats',
  activity: '/onboarding/activity',
  training: '/onboarding/training',
  summary: '/onboarding/summary',
};

const STEP_INDEX: Record<OnboardingStep, number> = {
  mode: 1,
  goal: 2,
  body_stats: 3,
  activity: 4,
  training: 5,
  summary: 6,
};

// Prerequisites are the completedSteps required (subset of the API enum, which
// excludes 'summary'). Summary needs activity but training is intentionally
// optional — the API allows completing onboarding in diet-only mode.
const STEP_PREREQUISITES: Record<
  OnboardingStep,
  ReadonlyArray<'goal' | 'body_stats' | 'activity'>
> = {
  mode: [],
  goal: [],
  body_stats: ['goal'],
  activity: ['goal', 'body_stats'],
  training: ['goal', 'body_stats', 'activity'],
  summary: ['goal', 'body_stats', 'activity'],
};

export function OnboardingLayout() {
  const location = useLocation();
  const setCurrentStep = useOnboardingStore((s) => s.setCurrentStep);
  const { data: profile, isLoading } = useOnboardingProfile();

  const currentStep = PATH_TO_STEP[location.pathname];

  useEffect(() => {
    if (currentStep) setCurrentStep(currentStep);
  }, [currentStep, setCurrentStep]);

  if (isLoading) {
    return (
      <div className="onboarding-shell">
        <div className="splash">
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  // Resumability: if the user lands on a step whose prerequisites haven't been
  // saved server-side yet, bounce them forward to the earliest missing step.
  if (profile && currentStep) {
    const prereqs = STEP_PREREQUISITES[currentStep];
    const missing = prereqs.find((p) => !profile.completedSteps.includes(p));
    if (missing) {
      return <Navigate to={STEP_TO_PATH[missing]} replace />;
    }
  }

  const stepNumber = currentStep ? STEP_INDEX[currentStep] : 1;

  return (
    <div className="onboarding-shell">
      <header className="onboarding-header">
        <div className="onboarding-header-left">
          <h1 className="onboarding-logo">OnFlow</h1>
        </div>
        <ProgressDots total={TOTAL_STEPS} current={stepNumber} />
        {/* Empty right cell preserves the 1fr-auto-1fr grid so the progress
            dots stay visually centered. The Back action lives in the in-card
            .onboarding-actions row, not the header. */}
        <div className="onboarding-header-right" aria-hidden />
      </header>
      <main className="onboarding-main">
        <Outlet />
      </main>
    </div>
  );
}
